import type { NavChild, NavItem, NavMenuProps } from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import { CheckboxField, ColorField, SelectField, TextField } from "./fields.js";
import { MediaField } from "./MediaField.js";
import type { WidgetInspectorProps } from "./registry.js";

const ALIGN = ["start", "center", "end"] as const;

function newId(prefix = "n"): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 8)}`;
}

export function NavMenuInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as NavMenuProps;
  const items = p.items ?? [];

  const setItems = (next: NavItem[]) => update(widget.id, { items: next });

  const addItem = () =>
    setItems([...items, { id: newId("n"), label: "New item", href: "/" }]);
  const updateItem = (id: string, patch: Partial<NavItem>) =>
    setItems(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const removeItem = (id: string) => setItems(items.filter((it) => it.id !== id));
  const moveItem = (id: string, dir: "up" | "down") => {
    const idx = items.findIndex((it) => it.id === id);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    const moved = next.splice(idx, 1)[0];
    if (!moved) return;
    next.splice(target, 0, moved);
    setItems(next);
  };

  const updateChildren = (itemId: string, children: NavChild[]) =>
    setItems(items.map((it) => (it.id === itemId ? { ...it, children } : it)));

  return (
    <>
      <TextField
        label="Brand text"
        value={p.brand ?? ""}
        onChange={(v) => update(widget.id, { brand: v || undefined })}
      />
      <TextField
        label="Brand URL"
        value={p.brandHref ?? ""}
        onChange={(v) => update(widget.id, { brandHref: v || undefined })}
      />
      <MediaField
        label="Logo URL"
        value={p.logoUrl ?? ""}
        onChange={(v) => update(widget.id, { logoUrl: v || undefined })}
      />
      <SelectField
        label="Items align"
        value={p.align ?? "end"}
        options={ALIGN}
        onChange={(v) => update(widget.id, { align: v as NavMenuProps["align"] })}
      />
      <CheckboxField
        label="Sticky header"
        value={p.sticky ?? false}
        onChange={(v) => update(widget.id, { sticky: v })}
      />
      <CheckboxField
        label="Bottom border"
        value={p.bordered ?? true}
        onChange={(v) => update(widget.id, { bordered: v })}
      />
      <ColorField
        label="Background"
        value={p.background ?? "#ffffff"}
        onChange={(v) => update(widget.id, { background: v })}
      />
      <ColorField
        label="Text"
        value={p.textColor ?? "#0f172a"}
        onChange={(v) => update(widget.id, { textColor: v })}
      />
      <ColorField
        label="Accent (CTA)"
        value={p.accent ?? "#111827"}
        onChange={(v) => update(widget.id, { accent: v })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        CTA
      </div>
      <TextField
        label="Label"
        value={p.cta?.label ?? ""}
        onChange={(v) =>
          update(widget.id, {
            cta: v ? { label: v, href: p.cta?.href ?? "/" } : undefined,
          })
        }
      />
      <TextField
        label="URL"
        value={p.cta?.href ?? ""}
        onChange={(v) =>
          update(widget.id, {
            cta: p.cta?.label ? { label: p.cta.label, href: v } : undefined,
          })
        }
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Menu items ({items.length})
      </div>

      {items.map((it, i) => {
        const children = it.children ?? [];
        return (
          <div
            key={it.id}
            style={{
              padding: "8px 16px 10px",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 4,
                justifyContent: "flex-end",
                marginBottom: 4,
              }}
            >
              <button
                type="button"
                className="emke-btn"
                onClick={() => moveItem(it.id, "up")}
                disabled={i === 0}
                style={{ padding: "2px 6px", opacity: i === 0 ? 0.4 : 1 }}
              >
                ↑
              </button>
              <button
                type="button"
                className="emke-btn"
                onClick={() => moveItem(it.id, "down")}
                disabled={i === items.length - 1}
                style={{
                  padding: "2px 6px",
                  opacity: i === items.length - 1 ? 0.4 : 1,
                }}
              >
                ↓
              </button>
              <button
                type="button"
                className="emke-btn is-danger"
                onClick={() => removeItem(it.id)}
                style={{ padding: "2px 6px" }}
              >
                ✕
              </button>
            </div>
            <TextField
              label={`Label ${i + 1}`}
              value={it.label}
              onChange={(v) => updateItem(it.id, { label: v })}
            />
            <TextField
              label="URL"
              value={it.href}
              onChange={(v) => updateItem(it.id, { href: v })}
            />
            <div
              style={{
                fontSize: 11,
                color: "var(--fg-dim)",
                marginBottom: 4,
              }}
            >
              Sub-items ({children.length})
            </div>
            {children.map((c, ci) => (
              <div
                key={c.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr auto",
                  gap: 4,
                  marginBottom: 4,
                }}
              >
                <input
                  type="text"
                  value={c.label}
                  onChange={(e) => {
                    const next = children.slice();
                    next[ci] = { ...c, label: e.target.value };
                    updateChildren(it.id, next);
                  }}
                  placeholder="Label"
                  style={{ width: "100%" }}
                />
                <input
                  type="text"
                  value={c.href}
                  onChange={(e) => {
                    const next = children.slice();
                    next[ci] = { ...c, href: e.target.value };
                    updateChildren(it.id, next);
                  }}
                  placeholder="URL"
                  style={{ width: "100%" }}
                />
                <button
                  type="button"
                  className="emke-btn is-danger"
                  onClick={() =>
                    updateChildren(
                      it.id,
                      children.filter((x) => x.id !== c.id),
                    )
                  }
                  style={{ padding: "2px 6px" }}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              className="emke-btn"
              onClick={() =>
                updateChildren(it.id, [
                  ...children,
                  { id: newId("nc"), label: "Sub-item", href: "/" },
                ])
              }
              style={{ width: "100%", marginTop: 4 }}
            >
              + Sub-item
            </button>
          </div>
        );
      })}

      <div style={{ padding: "12px 16px 0" }}>
        <button
          type="button"
          className="emke-btn"
          onClick={addItem}
          style={{ width: "100%" }}
        >
          + Add menu item
        </button>
      </div>
    </>
  );
}
